import {Injectable} from '@angular/core';
import * as _ from 'lodash';
import {getCellName} from '../../diagram-render/helpers/shapes-detectors.util';
import {menuItems, menuModel, subMenu} from './menu-items';
import {AuthService} from '../../core/auth/auth.service';
import {DiagramJsonHandlerService} from '../../diagram-data/diagram-json-handler.service';
import {MenuItem, MxMenuItemMeta} from 'src/app/shared/models/mx-menu-item-meta.interface';
import {BaseContextTool} from '../common/base-context-tool';

@Injectable()
export class ContextMenuService extends BaseContextTool {
  constructor(
    private jsonHandler: DiagramJsonHandlerService,
    private authSvc: AuthService
  ) {
    super();
  }

  public build(menu, cell, evt, special) {
    // it takes menu instance from mxGraph
    const graph = this.registry.getGraph();
    const isEnabled = graph.isEnabled();
    const {id} = this.authSvc.getUserDataSync();

    if (!isEnabled && getCellName(cell) === 'sticker' && cell.getJson()?.author?.id === id) {
      graph.setSelectionCell(cell);
    } else if (!isEnabled) {
      graph.clearSelection();
    }

    if (!isEnabled && getCellName(cell) === 'activity') {
      return;
    }

    let state;

    menu.smartSeparators = true;

    cell = graph.getSelectionCell();
    state = graph.view.getState(cell);

    if (!isEnabled) {
      if (this.isSelected.one && getCellName(cell) === 'sticker') {
        return this.addMenuItems(menu, menuModel.view.selection.one.sticker, null, evt);
      }

      return this.addMenuItems(menu, menuModel.view.canvas, null, evt);
    }

    if (special && cell.edge && cell.target && cell.source) {
      return;
    }

    if (!state) {
      state = {style: ''};
    }

    if (this.isSelected.nothing) {
      return this.addMenuItems(menu, menuModel.edit.canvas, null, evt);
    }

    if (this.isSelected.one) {
      const selectedCellName = getCellName(cell);
      let itemCollection;

      const getActivityMenuItemsList = () => {
        const activityItems = this.getActivityMenuItems();

        return this.checkForExceptions(cell, activityItems, this.authSvc);
      };

      switch (selectedCellName) {
        case 'connector':
          itemCollection = menuModel.edit.selection.one.connector;
          break;

        case 'terminator':
          itemCollection = menuModel.edit.selection.one.terminator;
          break;

        case 'activity':
          itemCollection = getActivityMenuItemsList();
          break;

        case 'text':
          itemCollection = menuModel.edit.selection.one.text;
          break;

        case 'sticker':
          itemCollection = menuModel.edit.selection.one.sticker;
          break;

        case 'image':
          itemCollection = menuModel.edit.selection.one.image;
          break;

        case 'flowline':
          itemCollection = this.checkForFLExceptions(
            cell,
            menuModel.edit.selection.one.flowline,
            this.authSvc
          );
          break;

        default:
          itemCollection = menuModel.edit.selection.one.flowline;
      }

      return this.addMenuItems(menu, itemCollection, null, evt);
    }

    if (this.isSelected.some) {
      let itemCollection;

      const selectedCellsNames = this.getSelectionNames();
      const selectionType =
        selectedCellsNames.length > 1 ? 'shuffle' : selectedCellsNames.join('&');
      // in case of specific multiselection (flowline and activity for example) we can handle it
      // by [...].join('&') and adding that case to switch operator

      switch (selectionType) {
        case 'shuffle':
          itemCollection = menuModel.edit.selection.some.shuffle;
          break;

        case 'terminator':
          itemCollection = menuModel.edit.selection.some.terminator;
          break;

        case 'connector':
          itemCollection = menuModel.edit.selection.some.connector;
          break;

        case 'activity':
          itemCollection = menuModel.edit.selection.some.activity;
          break;

        case 'text':
          itemCollection = menuModel.edit.selection.some.text;
          break;

        case 'image':
          itemCollection = menuModel.edit.selection.some.image;
          break;

        case 'flowline':
          itemCollection = menuModel.edit.selection.some.flowline;
          break;

        case 'sticker':
          itemCollection = menuModel.edit.selection.some.sticker;
          break;

        default:
          itemCollection = menuModel.edit.selection.some.flowline;
      }

      return this.addMenuItems(menu, itemCollection, null, evt);
    }
  }

  private addMenuItems(menu, items, parent, trigger?, sprites?): void {
    items.forEach((item, index: number) => {
      if (this.isSeparator(item)) {
        menu.addSeparator(parent);
      } else if (this.isSubmenuWithEnabledItems(item)) {
        this.addSubMenu(menu, item, parent);
      } else {
        const sprite = sprites?.[index] ?? null;

        this.addMenuItem(menu, item, parent, trigger, sprite);
      }
    });
  }

  private addMenuItem(menu, item, parent, trigger, sprite) {
    const action: MxMenuItemMeta = this.registry.getEditorUI().actions.get(item); // mxGraph

    if (action != null && (menu.showDisabled || action.isEnabled()) && action.visible) {
      const label = action.getDynamicLabel() || action.label;
      const isActive: boolean | null = action.isActive();
      const image: string | null = action.getImage();
      const {attributes} = action;
      const rowTitle: string | null = action.getRowTitle();
      const menuInstance: HTMLElement = menu.addItem(
        label,
        image,
        function () {
          action.funct(trigger);
        },
        parent,
        action.iconCls || null,
        action.isEnabled(),
        isActive
      );

      if (rowTitle) {
        menuInstance.setAttribute('title', rowTitle);
      }

      if (attributes) {
        for (const {name, value} of attributes) {
          menuInstance.setAttribute(name, value);
        }
      }

      if (typeof isActive === 'boolean' && !isActive) {
        menuInstance.classList.add('mxInActivePopupitem');
      }

      return menuInstance;
    }

    return null;
  }

  private checkForExceptions(cell, contextItems, auth) {
    const isProSpace = this.jsonHandler.isPro() || this.jsonHandler.isProTrial();
    const isArchitecture = this.diagramTypeConfigService.isArchitecture();

    return _.reduce(
      contextItems,
      function (accum, ctxItem) {
        if (!!ctxItem.title && ctxItem.title === 'addDocumentation') {
          if (isProSpace) {
            const menu = subMenu.addChange;

            accum.push(ctxItem);
            accum.push(menu);

            return accum;
          }
        }

        if (!!ctxItem.title && ctxItem.title === 'resourcePlacement') {
          if (!isArchitecture) {
            return accum;
          }
        }

        accum.push(ctxItem);

        return accum;
      },
      []
    );
  }

  private checkForFLExceptions(cell, contextItems, auth) {
    const isArchitecture = this.diagramTypeConfigService.isArchitecture();

    return _.reduce(
      contextItems,
      function (accum, ctxItem) {
        if (!!ctxItem.title && ctxItem.title === 'addToLine') {
          if (!isArchitecture) {
            return accum;
          }
        }

        accum.push(ctxItem);

        return accum;
      },
      []
    );
  }

  private isSeparator(item): boolean {
    return item === '-';
  }

  private isSubmenuWithEnabledItems(item): boolean {
    return this.isSubmenu(item) && this.hasEnabledSubmenuItems(item.items);
  }

  private isSubmenu(item): boolean {
    return !!item.items?.length;
  }

  private hasEnabledSubmenuItems(rootItems): boolean {
    const stack = [...rootItems];

    while (stack.length > 0) {
      const item = stack.pop();

      if (this.isSubmenu(item)) {
        stack.push(...item.items);
      } else {
        const action = this.registry.getEditorUI().actions.get(item);

        if (action?.isEnabled()) {
          return true;
        }
      }
    }

    return false;
  }

  private addSubMenu(menu, menuItem, parent): void {
    const title = this.registry.getResource(menuItem.title);
    const subMenu = menu.addItem(title, null, null, parent);

    this.addMenuItems(menu, menuItem.items, subMenu);
  }

  private getActivityMenuItems(): (MenuItem | string)[] {
    const items = _.cloneDeep(menuModel.edit.selection.one.activity) as (MenuItem | string)[];

    const drilldownOption = items.find(
      (item: MenuItem | string) =>
        typeof item === 'object' && item.title === menuItems.drilldownMenuTitle
    ) as MenuItem;

    if (drilldownOption) {
      drilldownOption.items = this.getDrilldownMenuItems();
    }

    return items;
  }
}
